/**
 * headlight-dashboards — configurable dashboards for pict apps.
 *
 * `install(pict, options)` adds everything a host needs: the loader (in embedded mode unless the host owns the page),
 * the action bus, the host provider, the dashboards view and (if the app has none) the searchable picker the host uses
 * to choose a project. The host then routes `routePatterns()` to the view and
 * registers handlers for the actions it can take.
 */
const libPictSectionPicker = require('pict-section-picker');

const DynamicDashboardLoader = require('./DynamicDashboardLoader.js');
const DashboardExporter = require('./DashboardExporter.js');
const HeadlightDashboardActions = require('./Headlight-Dashboard-Actions.js');
const HeadlightDashboardHost = require('./Headlight-Dashboard-Host.js');
const HeadlightDashboardsView = require('./Headlight-Dashboards-View.js');

/**
 * @typedef {Object} InstallOptions
 * @property {boolean} [AppChrome] - true only for the app that owns the page (the data app). Default false.
 * @property {string} [RouteBase] - Default '/Dashboards'.
 * @property {string} [DestinationAddress] - Where the dashboards view renders.
 * @property {'native'|'parent'|null} [Transport] - Where unhandled actions go.
 * @property {string} [ParentOrigin] - Required with Transport 'parent'.
 * @property {Array<string>|null} [TransportActions] - The actions the framing app can take; null for all.
 * @property {string} [LegacyDashboardsBaseURL] - Override for the app_dashboards host.
 * @property {() => number} [getCustomerID] - Where the host keeps the session's customer, when not in AppData.CurrentUser.
 * @property {() => string|null} [getFallbackRoute] - Where `RouteBase` alone goes for someone with no landing dashboard;
 *   without one they get the list of their dashboards.
 * @property {() => boolean} [getShowDescriptions] - true shows each open dashboard's Description under its title.
 * @property {(pRecordSet: string) => void} [onRecordSetPrepared] - Called with each dashboard recordset before it renders,
 *   so a micro app can allow its /PSRS/<RecordSet>/... routes (list paging and search use them).
 */

/**
 * @param {any} pPict
 * @param {InstallOptions} [pOptions]
 * @return {{ host: any, actions: any, view: any }}
 */
function install(pPict, pOptions)
{
	const tmpOptions = pOptions || {};
	if (!pPict.providers.DynamicDashboardLoader)
	{
		pPict.addProvider('DynamicDashboardLoader', { AppChrome: tmpOptions.AppChrome === true }, DynamicDashboardLoader);
	}
	if (!pPict.providers['Pict-Section-Picker'])
	{
		pPict.addProvider('Pict-Section-Picker', libPictSectionPicker.default_configuration, libPictSectionPicker);
	}
	if (!pPict.providers.DashboardExporter)
	{
		pPict.addProvider('DashboardExporter', {}, DashboardExporter);
	}
	if (!pPict.providers.HeadlightDashboardActions)
	{
		pPict.addProvider('HeadlightDashboardActions', { Transport: tmpOptions.Transport || null, ParentOrigin: tmpOptions.ParentOrigin || null, TransportActions: tmpOptions.TransportActions || null }, HeadlightDashboardActions);
	}
	if (!pPict.providers.HeadlightDashboardHost)
	{
		pPict.addProvider('HeadlightDashboardHost',
			{
				RouteBase: tmpOptions.RouteBase || '/Dashboards',
				LegacyDashboardsBaseURL: tmpOptions.LegacyDashboardsBaseURL || null,
				getCustomerID: tmpOptions.getCustomerID || null,
				getFallbackRoute: tmpOptions.getFallbackRoute || null,
				getShowDescriptions: tmpOptions.getShowDescriptions || null,
				onRecordSetPrepared: tmpOptions.onRecordSetPrepared || null,
			}, HeadlightDashboardHost);
	}
	if (!pPict.views.HeadlightDashboards)
	{
		const tmpViewOptions = tmpOptions.DestinationAddress ? { DefaultDestinationAddress: tmpOptions.DestinationAddress } : {};
		pPict.addView('HeadlightDashboards', tmpViewOptions, HeadlightDashboardsView);
	}
	const tmpHost = pPict.providers.HeadlightDashboardHost;
	const tmpActions = pPict.providers.HeadlightDashboardActions;
	if (!tmpActions.handlers['Dashboard.Open'])
	{
		tmpActions.registerHandler('Dashboard.Open', (pAction) => tmpHost.navigateTo(pAction.Hash, pAction.IDProject, pAction.Filters));
	}
	// A dashboard list can render outside the host's frame (an app's own /PSRS route), so its links are heard page-wide.
	if (typeof document !== 'undefined' && document.documentElement)
	{
		tmpActions.bindContainer(document.documentElement);
	}
	return { host: tmpHost, actions: tmpActions, view: pPict.views.HeadlightDashboards };
}

/**
 * Read a route payload into a view request.
 *
 * @param {{ data?: Record<string, string> }} pRouteData
 * @return {{ Hash?: string, IDProject?: string, Filter?: string, FilterExperience?: string, Offset?: string, PageSize?: string, Chooser?: boolean }}
 */
function requestFromRoute(pRouteData)
{
	const tmpData = (pRouteData && pRouteData.data) || {};
	if (tmpData.Hash === HeadlightDashboardHost.CHOOSER_HASH)
	{
		return { IDProject: tmpData.IDProject, Chooser: true };
	}
	return { Hash: tmpData.Hash, IDProject: tmpData.IDProject, Filter: tmpData.Filter, FilterExperience: tmpData.FilterExperience, Offset: tmpData.Offset, PageSize: tmpData.PageSize, Chooser: false };
}

/**
 * Register the dashboard routes with a host's router.
 *
 * @param {any} pPict
 * @param {(pRoute: string, fHandler: (pRouteData: any) => void) => void} fAddRoute - The host's addRoute.
 * @param {(pViewIdentifier: string) => void} fRenderView - The host's way of rendering a routed view.
 * @param {string} [pRouteBase]
 */
function addRoutes(pPict, fAddRoute, fRenderView, pRouteBase)
{
	const tmpBase = pRouteBase || '/Dashboards';
	for (const tmpPattern of HeadlightDashboardHost.routePatterns(tmpBase))
	{
		fAddRoute(tmpPattern, (pRouteData) =>
		{
			pPict.views.HeadlightDashboards.setRequest(requestFromRoute(pRouteData));
			fRenderView('HeadlightDashboards');
		});
	}
}

module.exports =
{
	install,
	addRoutes,
	requestFromRoute,
	routePatterns: HeadlightDashboardHost.routePatterns,
	CHOOSER_HASH: HeadlightDashboardHost.CHOOSER_HASH,
	ACTIONS: HeadlightDashboardActions.ACTIONS,
	DynamicDashboardLoader,
	DashboardExporter,
	HeadlightDashboardActions,
	HeadlightDashboardHost,
	HeadlightDashboardsView,
};
